import { useEffect, useMemo } from 'react';
import createMediaDetailsResolver from './mediaDetailsResolver';
/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';

/**
 * Internal dependencies
 */
import getResourceFromMedia3p from '../../utils/getResourceFromMedia3p';
import { ProviderType } from '../constants';
import { PROVIDERS } from '../providerConfiguration';
import Context from './context';
import { useApiFetcher } from './useApiFetcher';

/** @typedef {import('react').ProviderProps} ProviderProps */

/**
 * Provider for the Media3P API. Delegates fetching the data to apiFetcher,
 * but transforms the response into resources.
 *
 * @param {ProviderProps} Provider props.
 * @return {Object} Context.
 */
function Media3pApiProvider({ children }) {
  const MEDIA_PAGE_SIZE = 20;
  const apiFetcher = useApiFetcher();
  const { getMediaDetailsFromProvider } = apiFetcher;
  // Story, tenant and auth changes remount the editor, so the API client
  // bounds the resolver's lifetime.
  const details = useMemo(
    () =>
      createMediaDetailsResolver((...args) =>
        getMediaDetailsFromProvider(...args)
      ),
    [getMediaDetailsFromProvider]
  );
  useEffect(() => () => details.reset(), [details]);

  /**
   * Constructs the filter used for the listMedia requests.
   *
   * @param {Object} obj - An object with the options.
   * @param {?string} obj.folder Optional. Search term for a specific folder.
   * @param {?string} obj.provider The provider to get the media from.
   * Currently only 'unsplash' is supported.
   * @param {?string} obj.contentType Optional. The content type to send.
   * @param {?string} obj.player Optional. Search term for a specific player (ScorePlay)
   * @param {?string} obj.searchTerm Optional. Search term to send,
   * eg: 'cute cats'.
   * @param {?string} obj.tag Optional. Search term for a specific tag.
   * @param {?string} obj.categoryId Optional. Id of the selected
   * category.
   * @return {string} A filter string.
   */
  function constructFilter({
    folder,
    provider,
    contentType,
    player,
    searchTerm,
    categoryId,
    tag,
  }) {
    if (provider && !Object.keys(PROVIDERS).includes(provider)) {
      throw new Error(`Unsupported provider: ${provider}`);
    }

    if (categoryId && (player || searchTerm || tag || folder)) {
      throw new Error(
        `player/searchTerm/tag/folder and categoryId are mutually exclusive.`
      );
    }
    return [
      provider && `provider: ${provider}`,
      contentType && `type:${contentType}`,
      categoryId && `category:${categoryId}`,
      player && `player:${player}`,
      searchTerm,
      tag && `tag:${tag}`,
      folder && `folder:${folder}`,
    ]
      .filter(Boolean)
      .join(' ');
  }

  /**
   * Get media for the given parameters.
   *
   * @typedef {Object} ListMediaFilter
   * @property {?string} contentType Optional. The content type to filter.
   * @property {?string} searchTerm Optional. The search term to send,
   * eg: 'cute cats'.
   * @property {?string} categoryId Optional. Id of the selected category.
   * @type {import('./typedefs').ListMediaFn}
   * @param {Object} obj - An object with the options.
   * @param {string} obj.mediaProviderId The id of the provider to get the media from.
   * @param {?string} obj.provider The provider code (e.g., 'dropbox', 'unsplash').
   * @param {?ListMediaFilter} obj.filter Optional. The filter attributes.
   * @param {?string} obj.orderBy The desired ordering of the results.
   * Defaults to 'relevance' in the API.
   * @param {?string} obj.pageToken An optional page token to provide,
   * for pagination. If unspecified, the first page of results will be returned.
   * @return {Promise<{nextPageToken: *, media: *}>} An object with the media
   * resources and a next page token.
   */
  async function listMedia({
    mediaProviderId,
    filter,
    orderBy,
    pageToken,
    provider,
  }) {
    const { contentType, folder, player, searchTerm, categoryId, tag } =
      filter ?? {};
    let response = {};
    // Only ScorePlay pages carry detail summaries; other providers keep their
    // existing conversion and page-level error handling.
    const isScorePlay = provider === ProviderType.SCOREPLAY;
    // Library changes invalidate summaries. Query changes keep resolved details;
    // useFetchMediaEffect discards superseded pages.
    if (isScorePlay) details.setScope(mediaProviderId);

    // Check if this provider uses folder as a separate query parameter
    const providerConfig = provider ? PROVIDERS[provider] : null;
    const usesFolderParam = providerConfig?.usesFolderAsQueryParam && folder;

    response = await apiFetcher.listMedia({
      mediaProviderId,
      filter: constructFilter({
        contentType,
        player,
        searchTerm,
        categoryId,
        tag,
        // Only include folder in filter string if provider doesn't use it as query param
        folder: usesFolderParam ? null : folder,
      }),
      // Send folder as separate parameter for providers that support it
      folder: usesFolderParam ? folder : null,
      orderBy,
      pageSize: MEDIA_PAGE_SIZE,
      pageToken,
      provider,
    });

    const toResource = isScorePlay
      ? (m, index) =>
          details.register(
            mediaProviderId,
            m,
            `${pageToken || 'first'}:${index}`
          )
      : (m) => getResourceFromMedia3p(mediaProviderId, m);
    return {
      media: (response.media || []).map(toResource),
      nextPageToken: response.nextPageToken,
    };
  }

  /**
   * Get categories for the given parameters.
   *
   * @type {import('./typedefs').ListCategoriesFn}
   * @param {Object} obj - An object with the options.
   * @param {string} obj.provider The provider to get the media from.
   * @param {?string} obj.orderBy The desired ordering of the results.
   * Defaults to 'trending' in the API.
   * @return {Promise<{categories: *}>} An object with the category
   * resources.
   */
  async function listCategories({ provider, orderBy }) {
    const filter = constructFilter({ provider });
    const response = await apiFetcher.listCategories({
      filter,
      orderBy,
    });
    return {
      categories: response.categories.map((m) => ({
        id: m.name,
        label: m.displayName,
      })),
    };
  }

  /**
   * Register usage of a media for the given payload.
   *
   * @type {import('./typedefs').RegisterUsageFn}
   * @param {string} mediaProviderId The id of the media provider
   * @param {Object} obj - An object with the options.
   * @param {string} obj.registerUsageUrl The url to be called to register the
   * usage.
   * @return {Promise<undefined>} An object with the category
   * resources.
   */
  async function registerUsage(mediaProviderId, { registerUsageUrl }) {
    await apiFetcher.registerUsage(mediaProviderId, { registerUsageUrl });
  }

  /**
   * @type {import('./typedefs').Media3pApiContext}
   */
  const contextValue = {
    actions: {
      listMedia,
      details,
      listCategories,
      registerUsage,
    },
  };

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}

Media3pApiProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Media3pApiProvider;
